<template>
    <div class="login-page">
        <div class="login-card">
            <div class="login-brand">
                <h1 class="login-title">酒店供应商管理系统</h1>
                <p class="login-subtitle">请使用供应商编码与密码登录</p>
            </div>
            <el-form
                :model="ruleForm"
                :rules="rules"
                @submit.native.prevent
                ref="ruleForm"
                label-position="top"
                class="login-form"
            >
                <el-form-item label="账号" prop="username">
                    <el-input
                        v-model="ruleForm.username"
                        prefix-icon="el-icon-s-custom"
                        clearable
                        placeholder="请输入手机号"
                        autocomplete="username"
                        @keyup.enter.native="submitForm('ruleForm')"
                    />
                </el-form-item>
                <el-form-item label="密码" prop="password">
                    <el-input
                        v-model="ruleForm.password"
                        prefix-icon="el-icon-lock"
                        clearable
                        show-password
                        placeholder="请输入密码"
                        autocomplete="current-password"
                        @keyup.enter.native="submitForm('ruleForm')"
                    />
                </el-form-item>
                <el-form-item class="login-actions">
                    <el-button
                        type="primary"
                        class="login-btn"
                        @click="submitForm('ruleForm')"
                        native-type="submit"
                    >
                        登录
                    </el-button>
                </el-form-item>
                <div class="login-footer">
                    <span class="login-footer-hint">还没有账号？</span>
                    <a
                        :href="registerUrl"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="login-register-link"
                    >立即注册</a>
                </div>
            </el-form>
        </div>

        <!-- 微信内未授权：welcome 风格引导弹层 -->
        <div
            v-if="showWxGuideModal"
            class="wx-modals"
            @click.self="closeWxGuideModal"
        >
            <div class="wx-modal-box">
                <div class="wx-modal-icon-wrap">
                    <img
                        class="wx-modal-icon"
                        src="https://01-1256206408.cos.ap-beijing.myqcloud.com/images/configuration/d0a886a2583c4a133fe436b38de3f3b0.png"
                        alt=""
                    />
                </div>
                <div class="wx-modal-title">手机号快捷登录</div>
                <div class="wx-modal-desc">
                    航阳申请获取绑定手机号，点击确定获取
                </div>
                <div class="wx-modal-tip">
                    ● 获得你的公开信息(昵称、头像、地区及性别)
                </div>
                <el-button
                    type="primary"
                    class="wx-modal-btn"
                    :loading="wxRedirecting"
                    @click="startWechatOAuth"
                >
                    点击登录
                </el-button>
            </div>
        </div>

        <!-- 微信已授权但未绑定：手机号绑定弹层 -->
        <div
            v-if="showWxPhoneModal"
            class="wx-modals"
        >
            <div class="wx-modal-box">
                <div class="wx-modal-icon-wrap">
                    <img
                        class="wx-modal-icon"
                        src="https://01-1256206408.cos.ap-beijing.myqcloud.com/images/configuration/d0a886a2583c4a133fe436b38de3f3b0.png"
                        alt=""
                    />
                </div>
                <div class="wx-modal-title">绑定供应商手机号</div>
                <div class="wx-modal-desc">
                    请输入已在后台开通的供应商人员手机号，完成微信绑定
                </div>
                <el-input
                    v-model="wxPhone"
                    maxlength="11"
                    clearable
                    placeholder="请输入手机号"
                    class="wx-phone-input"
                />
                <div class="wx-sms-row">
                    <el-input
                        v-model="wxSmsCode"
                        maxlength="6"
                        clearable
                        placeholder="请输入短信验证码"
                        class="wx-sms-input"
                    />
                    <el-button
                        class="wx-sms-btn"
                        :disabled="wxSmsCountdown > 0 || wxSmsSending"
                        :loading="wxSmsSending"
                        @click="sendWxSmsCode"
                    >
                        {{ wxSmsCountdown > 0 ? wxSmsCountdown + "s" : "获取验证码" }}
                    </el-button>
                </div>
                <el-button
                    type="primary"
                    class="wx-modal-btn"
                    :loading="wxPhoneSubmitting"
                    @click="submitWxPhoneLogin"
                >
                    确认绑定并登录
                </el-button>
                <div class="wx-modal-alt">
                    <span>或使用</span>
                    <a href="javascript:;" @click="switchToPasswordLogin">账号密码登录</a>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import qs from "qs";
import { MessageBox } from "element-ui";
import { getRegisterApplyUrl } from "../api/api";
import { getToken } from "../assets/js/auth";
import { redirectAfterAuth } from "@/utils/orderDeepLink";
import {
    isWechatBrowser,
    getWechatOAuthCode,
    clearWechatOAuthCodeFromUrl,
    buildWechatAuthorizeUrl,
    getDefaultReturnUrl,
    getWechatAppId,
} from "../utils/wechatOAuth";

export default {
    name: "login",
    data() {
        return {
            registerUrl: getRegisterApplyUrl(),
            ruleForm: {
                username: "",
                password: "",
            },
            rules: {
                username: [
                    { required: true, message: "请输入账号", trigger: "blur" },
                ],
                password: [
                    { required: true, message: "请输入密码", trigger: "blur" },
                ],
            },
            wxCode: "",
            wxOpenId: "",
            wxUnionId: "",
            showWxGuideModal: false,
            showWxPhoneModal: false,
            wxRedirecting: false,
            wxPhoneSubmitting: false,
            wxPhone: "",
            wxSmsCode: "",
            wxSmsSending: false,
            wxSmsCountdown: 0,
            wxSmsTimer: null,
        };
    },
    beforeDestroy() {
        this.clearWxSmsTimer();
    },
    mounted() {
        try {
            MessageBox.close();
        } catch (e) {
            /* ignore */
        }
        if (typeof document !== "undefined") {
            document.body.classList.remove("el-popup-parent--hidden");
        }
        if (!isWechatBrowser()) {
            return;
        }
        const code = getWechatOAuthCode();
        if (code) {
            this.wxCode = code;
            this.tryWechatAutoLogin(code);
            return;
        }
        if (!getToken("userInfo")) {
            this.showWxGuideModal = true;
        }
    },
    methods: {
        submitForm(formName) {
            this.$refs[formName].validate((valid) => {
                if (valid) {
                    this.submit();
                }
            });
        },
        submit() {
            const loading = this.$loading({
                lock: true,
                text: "登录中…",
                spinner: "el-icon-loading",
                background: "rgba(0, 0, 0, 0.45)",
            });
            const payload = Object.assign({}, this.ruleForm);
            if (this.wxCode) {
                payload.wx_code = this.wxCode;
            }
            this.$axios
                .supplierLogin(qs.stringify(payload))
                .then((res) => {
                    loading.close();
                    if (res) {
                        this.onLoginSuccess(res);
                    }
                })
                .catch(() => {
                    loading.close();
                });
        },
        startWechatOAuth() {
            if (this.wxRedirecting) {
                return;
            }
            this.wxRedirecting = true;
            window.location.href = buildWechatAuthorizeUrl(getWechatAppId(), getDefaultReturnUrl());
        },
        tryWechatAutoLogin(code) {
            const loading = this.$loading({
                lock: true,
                text: "微信登录中…",
                spinner: "el-icon-loading",
                background: "rgba(0, 0, 0, 0.45)",
            });
            this.$axios
                .supplierWxLogin(qs.stringify({ code }))
                .then((res) => {
                    loading.close();
                    if (res && res.success && res.data) {
                        clearWechatOAuthCodeFromUrl();
                        this.onLoginSuccess(res.data);
                        return;
                    }
                    if (res && res.data && res.data.openid) {
                        this.wxOpenId = res.data.openid;
                        this.wxUnionId = res.data.unionid || "";
                    }
                    this.showWxPhoneModal = true;
                    if (res && res.msg) {
                        this.$message.warning(res.msg);
                    }
                })
                .catch(() => {
                    loading.close();
                    this.showWxPhoneModal = true;
                });
        },
        submitWxPhoneLogin() {
            const phone = (this.wxPhone || "").trim();
            const smsCode = (this.wxSmsCode || "").trim();
            if (!/^1[3456789]\d{9}$/.test(phone)) {
                this.$message.warning("请输入正确的手机号");
                return;
            }
            if (!smsCode) {
                this.$message.warning("请输入短信验证码");
                return;
            }
            if (!this.wxOpenId && !this.wxCode) {
                this.$message.warning("微信授权已失效，请重新点击登录");
                this.showWxPhoneModal = false;
                this.showWxGuideModal = true;
                return;
            }
            this.wxPhoneSubmitting = true;
            const bindPayload = { phone, sms_code: smsCode };
            if (this.wxOpenId) {
                bindPayload.openid = this.wxOpenId;
                bindPayload.unionid = this.wxUnionId;
            } else {
                bindPayload.code = this.wxCode;
            }
            this.$axios
                .supplierWxPhoneLogin(qs.stringify(bindPayload))
                .then((res) => {
                    this.wxPhoneSubmitting = false;
                    if (res && res.success && res.data) {
                        clearWechatOAuthCodeFromUrl();
                        this.showWxPhoneModal = false;
                        this.clearWxSmsTimer();
                        this.onLoginSuccess(res.data);
                        return;
                    }
                    this.$message.error((res && res.msg) || "绑定失败，请重试");
                })
                .catch(() => {
                    this.wxPhoneSubmitting = false;
                });
        },
        clearWxSmsTimer() {
            if (this.wxSmsTimer) {
                clearInterval(this.wxSmsTimer);
                this.wxSmsTimer = null;
            }
            this.wxSmsCountdown = 0;
        },
        startWxSmsCountdown() {
            this.clearWxSmsTimer();
            this.wxSmsCountdown = 60;
            this.wxSmsTimer = setInterval(() => {
                if (this.wxSmsCountdown <= 1) {
                    this.clearWxSmsTimer();
                    return;
                }
                this.wxSmsCountdown -= 1;
            }, 1000);
        },
        sendWxSmsCode() {
            const phone = (this.wxPhone || "").trim();
            if (!/^1[3456789]\d{9}$/.test(phone)) {
                this.$message.warning("请输入正确的手机号");
                return;
            }
            if (this.wxSmsCountdown > 0 || this.wxSmsSending) {
                return;
            }
            this.wxSmsSending = true;
            this.$axios
                .supplierWxSendSms(qs.stringify({ phone }))
                .then((res) => {
                    this.wxSmsSending = false;
                    if (res && res.success) {
                        this.$message.success("验证码已发送");
                        this.startWxSmsCountdown();
                        return;
                    }
                    this.$message.error((res && res.msg) || "发送失败，请重试");
                })
                .catch(() => {
                    this.wxSmsSending = false;
                });
        },
        onLoginSuccess(res) {
            if (res && res.need_select) {
                try {
                    sessionStorage.setItem(
                        "ebooking_supplier_select",
                        JSON.stringify({
                            da_id: res.da_id,
                            pre_token: res.pre_token,
                            suppliers: res.suppliers || [],
                        })
                    );
                } catch (e) {
                    this.$message.error("无法保存供应商列表，请重试");
                    return;
                }
                this.$router.replace({
                    path: "/select-supplier",
                    query: Object.assign({}, this.$route.query),
                });
                return;
            }
            this.setToken("userInfo", JSON.stringify(res));
            if (
                !redirectAfterAuth(this.$router, {
                    path: "/hotel",
                    query: Object.assign({}, this.$route.query),
                })
            ) {
                this.$router.replace({
                    path: "/hotel",
                    query: Object.assign({}, this.$route.query),
                });
            }
        },
        closeWxGuideModal() {
            this.showWxGuideModal = false;
        },
        switchToPasswordLogin() {
            this.showWxPhoneModal = false;
        },
    },
};
</script>

<style scoped>
.login-page {
    width: 100%;
    min-height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: max(24px, env(safe-area-inset-top, 0px)) 16px
        max(24px, env(safe-area-inset-bottom, 0px));
    background: linear-gradient(145deg, #5d7cba 0%, #93a5cf 38%, #dce8e4 100%);
    box-sizing: border-box;
    position: relative;
}

.login-card {
    width: 100%;
    max-width: 420px;
    padding: 36px 36px 28px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.96);
    box-shadow: 0 12px 40px rgba(45, 62, 93, 0.18);
    border: 1px solid rgba(255, 255, 255, 0.8);
    box-sizing: border-box;
}

.login-brand {
    text-align: center;
    margin-bottom: 8px;
}

.login-title {
    margin: 0;
    font-size: 22px;
    font-weight: 600;
    color: #303133;
    letter-spacing: 0.02em;
    line-height: 1.35;
}

.login-subtitle {
    margin: 10px 0 0;
    font-size: 13px;
    color: #909399;
    line-height: 1.5;
}

.login-form {
    margin-top: 22px;
}

.login-form >>> .el-form-item {
    margin-bottom: 18px;
}

.login-form >>> .el-form-item__label {
    padding: 0 0 6px;
    line-height: 1.4;
    color: #606266;
}

.login-actions {
    margin-bottom: 8px !important;
}

.login-btn {
    width: 100%;
    padding: 12px 20px;
    font-size: 15px;
    border-radius: 6px;
}

.login-footer {
    text-align: center;
    padding-top: 8px;
    font-size: 13px;
    color: #909399;
}

.login-footer-hint {
    margin-right: 4px;
}

.login-register-link {
    color: #409eff;
    text-decoration: none;
    font-weight: 500;
}

.login-register-link:hover {
    text-decoration: underline;
    color: #66b1ff;
}

/* welcome 风格微信弹层 */
.wx-modals {
    position: fixed;
    inset: 0;
    z-index: 2000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px 16px;
    background-color: rgba(0, 0, 0, 0.6);
    box-sizing: border-box;
}

.wx-modal-box {
    width: 100%;
    max-width: 340px;
    padding: 24px 22px 20px;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
    box-sizing: border-box;
}

.wx-modal-icon-wrap {
    text-align: center;
    margin-bottom: 12px;
}

.wx-modal-icon {
    width: 72px;
    height: 72px;
}

.wx-modal-title {
    text-align: center;
    font-size: 17px;
    font-weight: 600;
    color: #303133;
    padding-bottom: 12px;
    border-bottom: 1px solid #ebeef5;
}

.wx-modal-desc {
    margin-top: 12px;
    font-size: 14px;
    color: #606266;
    line-height: 1.5;
    text-align: center;
}

.wx-modal-tip {
    margin-top: 10px;
    font-size: 13px;
    color: #909399;
    line-height: 1.5;
}

.wx-phone-input {
    margin-top: 16px;
}

.wx-sms-row {
    display: flex;
    align-items: center;
    margin-top: 12px;
    gap: 8px;
}

.wx-sms-input {
    flex: 1;
    min-width: 0;
}

.wx-sms-btn {
    flex-shrink: 0;
    padding: 10px 12px;
}

.wx-modal-btn {
    width: 100%;
    margin-top: 16px;
    padding: 12px 20px;
    font-size: 15px;
    border-radius: 22px;
}

.wx-modal-alt {
    margin-top: 14px;
    text-align: center;
    font-size: 13px;
    color: #909399;
}

.wx-modal-alt a {
    color: #409eff;
    text-decoration: none;
    margin-left: 4px;
}
</style>



// WEBPACK FOOTER //
// src/components/login.vue